import { useMemo, useState } from "react";
import EditModal from "../ui/EditModal";
import { formatDate } from "../../utils/dateUtils";

const AllergyTable = ({ data, onUpdate, onDelete }) => {
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(5);
  const [sortDir, setSortDir] = useState("desc");
  const [editingItem, setEditingItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const sorted = useMemo(() => {
    return data.slice().sort((a, b) => {
      if (a.date < b.date) return sortDir === "asc" ? -1 : 1;
      if (a.date > b.date) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortDir]);

  const pageCount = Math.ceil(sorted.length / rowsPerPage);
  const pageData = sorted.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const toggleSort = () => {
    setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    setPage(1);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleSave = async (formData) => {
    setIsSaving(true);
    try {
      await onUpdate(editingItem.id, formData);
      setIsModalOpen(false);
      setEditingItem(null);
    } catch {
      alert("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this allergy record?")) {
      try {
        await onDelete(id);
      } catch {
        alert("Failed to delete entry");
      }
    }
  };

  const getSeverityBadge = (severity) => {
    const map = {
      mild: "bg-success",
      moderate: "bg-warning text-dark",
      severe: "bg-danger",
      anaphylaxis: "bg-dark",
    };
    return map[severity] || "bg-secondary";
  };

  const editFields = [
    { name: "date", label: "Date Observed", type: "datetime-local", required: true },
    { name: "foodItem", label: "Food Item", type: "text", required: true },
    { name: "reaction", label: "Reaction", type: "text" },
    {
      name: "severity", label: "Severity", type: "select", required: true,
      options: [
        { label: "Mild", value: "mild" },
        { label: "Moderate", value: "moderate" },
        { label: "Severe", value: "severe" },
        { label: "Anaphylaxis", value: "anaphylaxis" },
      ],
    },
    { name: "notes", label: "Notes", type: "text" },
  ];

  return (
    <>
      <div className="alert alert-warning mb-3">
        <strong>Known Allergens:</strong> {data.length} recorded
      </div>

      <div className="table-responsive">
        <table className="table table-sm table-striped table-hover">
          <thead className="table-light">
            <tr>
              <th style={{ cursor: "pointer" }} onClick={toggleSort}>
                Date {sortDir === "desc" ? "▼" : "▲"}
              </th>
              <th>Food Item</th>
              <th>Reaction</th>
              <th>Severity</th>
              <th>Notes</th>
              <th style={{ width: "80px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageData.map((row) => (
              <tr key={row.id}>
                <td>{formatDate(row.date)}</td>
                <td><strong>{row.foodItem}</strong></td>
                <td>{row.reaction || <span className="text-muted">-</span>}</td>
                <td>
                  <span className={`badge ${getSeverityBadge(row.severity)}`}>
                    {row.severity}
                  </span>
                </td>
                <td>{row.notes || <span className="text-muted">-</span>}</td>
                <td>
                  <button
                    className="btn btn-xs btn-warning me-1"
                    title="Edit"
                    onClick={() => handleEdit(row)}
                    style={{ padding: "0.25rem 0.5rem", fontSize: "0.875rem" }}
                  >
                    ✎
                  </button>
                  <button
                    className="btn btn-xs btn-danger"
                    title="Delete"
                    onClick={() => handleDelete(row.id)}
                    style={{ padding: "0.25rem 0.5rem", fontSize: "0.875rem" }}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <nav>
          <ul className="pagination pagination-sm">
            <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => setPage((p) => Math.max(p - 1, 1))}>Previous</button>
            </li>
            {[...Array(pageCount)].map((_, idx) => (
              <li key={idx + 1} className={`page-item ${idx + 1 === page ? "active" : ""}`}>
                <button className="page-link" onClick={() => setPage(idx + 1)}>{idx + 1}</button>
              </li>
            ))}
            <li className={`page-item ${page === pageCount ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => setPage((p) => Math.min(p + 1, pageCount))}>Next</button>
            </li>
          </ul>
        </nav>
      )}

      <EditModal
        isOpen={isModalOpen}
        item={editingItem}
        title="Edit Allergy Record"
        fields={editFields}
        onSave={handleSave}
        onCancel={() => { setIsModalOpen(false); setEditingItem(null); }}
        isLoading={isSaving}
      />
    </>
  );
};

export default AllergyTable;
